import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { DripCampaignTemplate, DripCampaignStep } from '@app/_models/drip-campaign';
import { DripCampaignTemplateService } from '../drip-campaign-template.service';
import { MsgTemplateService } from '@app/_components/msg-template/msg-template.service';
import { AlertService } from '@app/_services';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

interface StepRow {
  dripCampaignStepId?: number;
  stepOrder: number;
  channel: string;
  delayHours: number;
  msgTemplateId?: number;
  stopOnExecute: boolean;
  actionPayload?: string;
  // parsed action payload fields (channel-specific)
  taskName?: string;
  dueHoursOffset?: number;
  noteText?: string;
  finalStatus?: string;
  // msg template search
  msgTemplateOptions?: any[];
  msgTemplateLoading?: boolean;
}

@Component({
  selector: 'app-drip-campaign-template-edit',
  templateUrl: './drip-campaign-template-edit.component.html',
  providers: [MessageService],
  standalone: false
})
export class DripCampaignTemplateEditComponent implements OnInit {

  form: UntypedFormGroup;
  templateId: number;
  loading = false;
  saving = false;

  steps: StepRow[] = [];

  taskSourceOptions = [
    { value: null, label: 'Any' },
    { value: 4, label: 'Quote' },
    { value: 5, label: 'Owner Lead' },
    { value: 6, label: 'Owner Statement' },
    { value: 7, label: 'Onboarding' }
  ];

  channelOptions = [
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
    { value: 'vrbo', label: 'VRBO' },
    { value: 'phoneTask', label: 'Phone Task' },
    { value: 'note', label: 'Note' },
    { value: 'markStatus', label: 'Mark Status' }
  ];

  finalStatusOptions = [
    { value: 'ACTIVE', label: 'ACTIVE' },
    { value: 'PAUSED', label: 'PAUSED' },
    { value: 'COMPLETED', label: 'COMPLETED' },
    { value: 'COLD', label: 'COLD' },
    { value: 'CANCELLED', label: 'CANCELLED' }
  ];

  msgChannels = ['email', 'sms', 'vrbo'];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: UntypedFormBuilder,
    private svc: DripCampaignTemplateService,
    private msgTemplateSvc: MsgTemplateService,
    private alertService: AlertService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.templateId = this.route.snapshot.params['id'] ? +this.route.snapshot.params['id'] : null;
    if (this.templateId) {
      this.loadTemplate();
    }
  }

  buildForm() {
    this.form = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      applicableTaskSource: [null],
      active: [true]
    });
  }

  loadTemplate() {
    this.loading = true;
    forkJoin({
      template: this.svc.findById(this.templateId).pipe(catchError(() => of(null))),
      steps: this.svc.findStepsByTemplateId(this.templateId).pipe(catchError(() => of([])))
    }).pipe(first()).subscribe(({ template, steps }) => {
      if (template) {
        this.form.patchValue({
          name: template.name,
          description: template.description,
          applicableTaskSource: template.applicableTaskSource ?? null,
          active: template.active ?? true
        });
      }
      this.steps = (steps || []).map((s, i) => this.stepVoToRow(s, i + 1));
      this.loading = false;
    });
  }

  stepVoToRow(s: DripCampaignStep, order: number): StepRow {
    const row: StepRow = {
      dripCampaignStepId: s.dripCampaignStepId,
      stepOrder: order,
      channel: s.channel || 'email',
      delayHours: s.delayHours ?? 0,
      msgTemplateId: s.msgTemplateId,
      stopOnExecute: s.stopOnExecute ?? false,
      actionPayload: s.actionPayload,
      msgTemplateOptions: [],
      msgTemplateLoading: false
    };
    if (s.actionPayload) {
      try {
        const p = JSON.parse(s.actionPayload);
        row.taskName = p.taskName;
        row.dueHoursOffset = p.dueHoursOffset;
        row.noteText = p.noteText;
        row.finalStatus = p.finalStatus;
      } catch (_) {}
    }
    return row;
  }

  addStep() {
    this.steps = [...this.steps, {
      stepOrder: this.steps.length + 1,
      channel: 'email',
      delayHours: 24,
      stopOnExecute: false,
      msgTemplateOptions: [],
      msgTemplateLoading: false
    }];
  }

  removeStep(index: number) {
    this.steps = this.steps.filter((_, i) => i !== index);
    this.reorderSteps();
  }

  moveUp(index: number) {
    if (index === 0) { return; }
    const arr = [...this.steps];
    [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
    this.steps = arr;
    this.reorderSteps();
  }

  moveDown(index: number) {
    if (index === this.steps.length - 1) { return; }
    const arr = [...this.steps];
    [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
    this.steps = arr;
    this.reorderSteps();
  }

  reorderSteps() {
    this.steps = this.steps.map((s, i) => ({ ...s, stepOrder: i + 1 }));
  }

  isMsgChannel(channel: string): boolean {
    return this.msgChannels.includes(channel);
  }

  onChannelChange(step: StepRow) {
    // clear channel-specific fields when switching
    step.msgTemplateId = null;
    step.taskName = null;
    step.dueHoursOffset = null;
    step.noteText = null;
    step.finalStatus = null;
    step.msgTemplateOptions = [];
  }

  searchMsgTemplates(event: any, step: StepRow) {
    step.msgTemplateLoading = true;
    const mediumCode = step.channel === 'email' ? 1 : step.channel === 'sms' ? 2 : null;
    const filters: any = { name: { value: event.query, matchMode: 'contains' } };
    if (mediumCode != null) {
      filters['communicationMediumCode'] = { value: mediumCode, matchMode: 'equals' };
    }
    this.msgTemplateSvc.findByFilter(0, 30, 'name', 1, null, null, filters)
      .pipe(first())
      .subscribe(
        (res: any) => {
          step.msgTemplateOptions = res?.data?.msgTemplates ?? [];
          step.msgTemplateLoading = false;
        },
        () => { step.msgTemplateLoading = false; }
      );
  }

  onMsgTemplateSelect(event: any, step: StepRow) {
    step.msgTemplateId = event?.msgTemplateId ?? null;
  }

  buildActionPayload(step: StepRow): string | null {
    if (step.channel === 'phoneTask') {
      return JSON.stringify({ taskName: step.taskName || '', dueHoursOffset: step.dueHoursOffset ?? 0 });
    }
    if (step.channel === 'note') {
      return JSON.stringify({ noteText: step.noteText || '' });
    }
    if (step.channel === 'markStatus') {
      return JSON.stringify({ finalStatus: step.finalStatus || 'ACTIVE' });
    }
    return null;
  }

  onSave() {
    this.form.markAllAsTouched();
    if (this.form.invalid) { return; }

    this.saving = true;
    const templatePayload: DripCampaignTemplate = {
      dripCampaignTemplateId: this.templateId,
      name: this.form.value.name,
      description: this.form.value.description || null,
      applicableTaskSource: this.form.value.applicableTaskSource ?? null,
      active: this.form.value.active
    };

    const saveOrUpdate$ = this.templateId
      ? this.svc.update(templatePayload)
      : this.svc.save(templatePayload);

    saveOrUpdate$.pipe(first()).subscribe(
      (saved: DripCampaignTemplate) => {
        const tid = saved?.dripCampaignTemplateId ?? this.templateId;
        this.saveSteps(tid);
      },
      (err: any) => {
        this.alertService.error(err);
        this.saving = false;
      }
    );
  }

  saveSteps(templateId: number) {
    if (this.steps.length === 0) {
      this.saving = false;
      this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Template saved.', life: 3000 });
      setTimeout(() => this.router.navigate(['/drip-campaign-templates']), 1200);
      return;
    }

    const stepObs = this.steps.map((step, i) => {
      const payload: DripCampaignStep = {
        dripCampaignStepId: step.dripCampaignStepId,
        dripCampaignTemplateId: templateId,
        stepOrder: i + 1,
        channel: step.channel,
        delayHours: step.delayHours ?? 0,
        msgTemplateId: this.isMsgChannel(step.channel) ? (step.msgTemplateId || null) : null,
        actionPayload: this.buildActionPayload(step),
        stopOnExecute: step.stopOnExecute ?? false
      };
      if (step.dripCampaignStepId) {
        return this.svc.updateStep(payload).pipe(catchError(e => { console.error('step update error', e); return of(null); }));
      } else {
        return this.svc.saveStep(payload).pipe(catchError(e => { console.error('step save error', e); return of(null); }));
      }
    });

    forkJoin(stepObs).pipe(first()).subscribe(
      () => {
        this.saving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Template and steps saved.', life: 2000 });
        setTimeout(() => this.router.navigate(['/drip-campaign-templates']), 1200);
      },
      (err: any) => {
        this.alertService.error(err);
        this.saving = false;
      }
    );
  }

  onCancel() {
    this.router.navigate(['/drip-campaign-templates']);
  }
}
